import { ChangeDetectionStrategy, Component, Injector, Input, ViewEncapsulation } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { FiDropdownItemAdditionalTextFn, FiDropdownItemDisabledFn } from '@fi-shared/fi-select-search/fi-select-search';
import { IChecklist2UnitWithPeriodModel } from '@fi-shared/checklist/checklist-v2';
import { FiDateTimeFormatOptions, isDefined } from '@fi-shared/utils';
import { AppTextService } from '@fi-shared/app-text';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';

@Component({
    selector: 'fi-checklist2-viewer-unit-selector',
    templateUrl: './checklist-viewer-unit-selector.component.html',
    styleUrls: ['./checklist-viewer-unit-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist2-viewer-unit-selector' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiChecklist2ViewerUnitSelectorComponent,
            multi: true,
        },
    ],
    standalone: false,
})
export class FiChecklist2ViewerUnitSelectorComponent extends ControlValueAccessorValidatorBase<number> {
    @Input({ required: true }) auditMode: boolean;
    @Input({ required: true }) businessUnits: IChecklist2UnitWithPeriodModel[] = [];
    @Input() headingText: string;
    @Input() descriptionText: string;
    @Input() selectLabel: string;
    @Input() isSelectDisabled: boolean;
    @Input() isSelectRequired: boolean;

    constructor(
        injector: Injector,
        private appTextService: AppTextService,
        private fiFormatDateService: FiFormatDateService,
    ) {
        super(injector);
    }

    isBusinessUnitDisabledFn: FiDropdownItemDisabledFn<IChecklist2UnitWithPeriodModel> = (
        item: IChecklist2UnitWithPeriodModel,
        allItems: IChecklist2UnitWithPeriodModel[],
    ) => {
        if (!item.IsActivated) return true;

        if (!this.auditMode) {
            return false;
        }

        if (!isDefined(item.LastPeriod)) return false;

        return this.isAuditInProcess(item.LastPeriod.DueDateTime);
    };

    getOptionAdditionalTextFn: FiDropdownItemAdditionalTextFn<IChecklist2UnitWithPeriodModel> = (
        item: IChecklist2UnitWithPeriodModel,
        allItems: IChecklist2UnitWithPeriodModel[],
    ): string => {
        if (!item.IsActivated) {
            return this.appTextService.get('common.deactivated');
        }

        if (!this.auditMode) {
            return '';
        }

        if (!item.hasOwnProperty('LastPeriod')) return '';

        if (this.isAuditInProcess(item.LastPeriod.DueDateTime)) {
            return (
                '<span class="audit-in-process-text">' +
                this.appTextService.get('checklist.ongoing') +
                '</span>' +
                this.appTextService.get('checklist.due_date') +
                ': ' +
                this.getPeriodDate(item.LastPeriod.DueDateTime)
            );
        }

        if (!this.auditMode) {
            return (
                this.appTextService.get('common.latest_submission') +
                ': ' +
                this.getPeriodDate(item.LastPeriod.DueDateTime)
            );
        }

        let text =
            this.appTextService.get('checklist.latest_audit') + ': ' + this.getPeriodDate(item.LastPeriod.DueDateTime);

        if (item.TotalPeriodsCount > 1) {
            text += '<br> ' + this.appTextService.get('checklist.total_audits') + ': ' + item.TotalPeriodsCount;
        }
        return text;
    };

    getOptionAdditionalClassFn: FiDropdownItemAdditionalTextFn<IChecklist2UnitWithPeriodModel> = (
        item: IChecklist2UnitWithPeriodModel,
        allItems: IChecklist2UnitWithPeriodModel[],
    ): string => {
        if (!item.IsActivated) {
            return 'red-color';
        }
    };

    private isAuditInProcess(dueDateTime: string): boolean {
        const currentDateTimestamp: number = new Date().getTime();
        const dueDateTimestamp: number = new Date(dueDateTime).getTime();
        return dueDateTimestamp > currentDateTimestamp;
    }

    private getPeriodDate(dateTime: string): string {
        return this.fiFormatDateService.formatDate(new Date(dateTime), FiDateTimeFormatOptions, {
            showToday: false,
            showUTC: true,
        });
    }

    getUnitSelectionText() {
        let selectionText = '';
        const businessUnit = this.businessUnits.find((bu) => bu.BusinessUnitId == this.ngModel);
        if (businessUnit) {
            selectionText = businessUnit.BusinessUnitName;
        }
        return selectionText;
    }
}
